// Validation patterns
export const VALIDATION_PATTERNS = {
  ENTITY_NAME: /^[a-zA-Z\s]+$/,
  NAME: /^[a-zA-Z\s'-]+$/,
  EMAIL: /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/
}

// Validation error messages
export const VALIDATION_MESSAGES = {
  REQUIRED: (fieldName) => `${fieldName} is required`,
  INVALID_NAME: 'Only letters, spaces, hyphens, and apostrophes are allowed',
  INVALID_ENTITY_NAME: 'Only letters and spaces are allowed',
  INVALID_EMAIL: 'Please enter a valid email address',
  MAX_LENGTH: (maxLength) => `Must be less than ${maxLength} characters`
}

/**
 * Validates a name field (first name, last name)
 * @param {string} value - The value to validate
 * @param {string} fieldName - The field name for error messages (e.g., 'First name')
 * @param {number} maxLength - Maximum allowed length (default: 50)
 * @returns {string} Error message or empty string if valid
 */
export const validateName = (value, fieldName = 'Name', maxLength = 50) => {
  if (!value.trim()) {
    return VALIDATION_MESSAGES.REQUIRED(fieldName)
  }
  if (!VALIDATION_PATTERNS.NAME.test(value)) {
    return VALIDATION_MESSAGES.INVALID_NAME
  }
  if (value.length > maxLength) {
    return VALIDATION_MESSAGES.MAX_LENGTH(maxLength)
  }
  return ''
}

/**
 * Validates a client name field
 * @param {string} value - The value to validate
 * @param {number} maxLength - Maximum allowed length (default: 50)
 * @returns {string} Error message or empty string if valid
 */
export const validateClientName = (value, maxLength = 50) => {
  if (!value.trim()) {
    return VALIDATION_MESSAGES.REQUIRED('Client name')
  }
  if (!VALIDATION_PATTERNS.ENTITY_NAME.test(value)) {
    return VALIDATION_MESSAGES.INVALID_ENTITY_NAME
  }
  if (value.length > maxLength) {
    return VALIDATION_MESSAGES.MAX_LENGTH(maxLength)
  }
  return ''
}

/**
 * Validates an email address
 * @param {string} value - The email address to validate
 * @returns {string} Error message or empty string if valid
 */
export const validateEmail = (value) => {
  if (!value.trim()) {
    return VALIDATION_MESSAGES.REQUIRED('Email')
  }
  if (!VALIDATION_PATTERNS.EMAIL.test(value)) {
    return VALIDATION_MESSAGES.INVALID_EMAIL
  }
  return ''
}

/**
 * Validates a form field based on its type
 * @param {string} fieldType - Type of field ('firstName', 'lastName', 'email', 'clientName')
 * @param {string} value - The value to validate
 * @returns {string} Error message or empty string if valid
 */
export const validateField = (fieldType, value) => {
  switch (fieldType) {
    case 'firstName':
      return validateName(value, 'First name')
    case 'lastName':
      return validateName(value, 'Last name')
    case 'email':
      return validateEmail(value)
    case 'clientName':
      return validateClientName(value)
    default:
      return ''
  }
}

/**
 * Validates a form with multiple fields
 * @param {Object} formData - Object containing form field values
 * @param {Array} fields - Array of field names to validate
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validateForm = (formData, fields) => {
  const errors = {}
  for (const field of fields) {
    errors[field] = validateField(field, formData[field])
  }
  return errors
}

/**
 * Checks if a form is valid (no errors)
 * @param {Object} formData - Object containing form field values
 * @param {Object} errors - Object containing error messages
 * @param {Array} requiredFields - Array of required field names
 * @returns {boolean} True if form is valid, false otherwise
 */
export const isFormValid = (formData, errors, requiredFields) => {
  // Check if all required fields have values
  const hasAllValues = requiredFields.every((field) => formData[field]?.trim())

  // Check if there are no errors
  const hasNoErrors = requiredFields.every((field) => !errors[field])

  return hasAllValues && hasNoErrors
}
